import { useCallback, useEffect, useRef, useState } from 'react';
import { useQlubRouter } from '../../../hook/router';
import { ChangeHandler } from '../../PhoneInputV2';
import * as events from '../../../const/events';
import { ILoginCallbacks, ILoginOptions } from '../../../context/auth/types';
import { IProfile, ISendSmsResponse } from '../../../repository/auth/type';
import AuthManager from '../../../repository/auth';
import VendorAPIManager from '../../../repository/vendor';
import { useTranslation } from '../../../hook/translations';
import { getRandomString } from '../../../utility/random';
import { snackBar } from '../../../hook/snackBar';
import { setAuth } from '../../../service/session/auth';
import { setRedirection } from './utils';
import { profileEventsCenter } from '../../../hook/eventsCenter';

export interface IAppleData {
    authorization: {
        state: string;
        code: string;
        id_token: string;
    };
    user: {
        email: string;
        name: {
            firstName: string;
            lastName: string;
        };
    };
}

interface IProps {
    getRecaptchaToken: (reserve?: boolean) => Promise<string | null>;
    setOpenFn: (fn: (options?: ILoginOptions) => Promise<any>) => void;
    fullPage?: () => boolean;
    onLogin: (login: boolean, profile: IProfile) => void;
    loginCallbacks: ILoginCallbacks;
}

export const dataManager = ({ getRecaptchaToken, setOpenFn, fullPage, onLogin, loginCallbacks }: IProps) => {
    const authManager = AuthManager.getInstance();

    const { t } = useTranslation('common');
    const promise = useRef<{
        resolve?: any;
        reject?: any;
    }>({});

    const [open, setOpen] = useState(false);
    const [step, setStep] = useState<'phone' | 'otp' | 'done'>('phone');
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    const [smsRes, setSmsRes] = useState<ISendSmsResponse | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState<ILoginOptions | undefined>(undefined);
    const [key, setKey] = useState(getRandomString());
    const [resendTimer, setResendTimer] = useState(false);
    const [phoneErr, setPhoneErr] = useState<string | undefined>(undefined);
    const [validPhone, setIsValidPhone] = useState<boolean | undefined>(undefined);
    const [optErr, setOptErr] = useState<string | undefined>(undefined);
    const [appleLoading, setAppleLoading] = useState<boolean>(false);
    const [googleLoading, setGoogleLoading] = useState<boolean>(false);

    const { url } = useQlubRouter();

    const [hasConsented, setHasConsented] = useState<boolean>(url?.cc === 'ae');

    const { triggerSnackBar: enqueueSnackbar, actionButton, triggerAlternateSnackBar } = snackBar();
    const {
        userProfile: { attemptToLoginEvent, resendOPTEvent, successfulLoginEvent },
    } = profileEventsCenter();

    useEffect(() => {
        setHasConsented(url?.cc === 'ae');
    }, [url?.cc]);

    const openHandler = useCallback(
        (opt?: ILoginOptions) => {
            return new Promise((resolve, reject) => {
                if (promise.current) {
                    promise.current.resolve = resolve;
                    promise.current.reject = reject;
                }
                setHasConsented(url?.cc === 'ae');
                setStep('phone');
                setOpen(true);
                setOptions(opt);
            });
        },
        [open, fullPage, url?.cc],
    );

    useEffect(() => {
        setOpenFn?.(openHandler);
        setKey(getRandomString());
    }, [openHandler, fullPage]);

    const resendSmsHandler = () => {
        if (!smsRes || loading) {
            return;
        }
        setResendTimer(false);
        setLoading(true);
        setOptErr(undefined);
        setCode('');
        getRecaptchaToken()
            .then((token) => {
                if (!token) {
                    return Promise.reject(new Error('invalid token!'));
                }
                return authManager
                    .resendSms({
                        smsID: smsRes.smsID,
                        reCaptcha: token,
                    })
                    .then(() => {
                        setResendTimer(true);
                        enqueueSnackbar(
                            t('Sms sent successfully'),
                            {
                                variant: 'success',
                                action: actionButton,
                                autoHideDuration: 3000,
                            },
                            true,
                        );
                    });
            })
            .catch((err) => {
                promise.current?.reject?.(err);
            })
            .finally(() => {
                setLoading(false);
                resendOPTEvent();
            });
    };

    const loginHandler = useCallback(() => {
        attemptToLoginEvent('phone');

        if (loading) {
            return;
        }

        if (validPhone !== true) {
            setPhoneErr(getError('INVALID_PHONE'));
            return;
        }

        setPhoneErr(undefined);
        setOptErr(undefined);
        setLoading(true);

        getRecaptchaToken()
            .then(async (token) => {
                if (!token) {
                    return Promise.reject(new Error('invalid token!'));
                }
                const proceed = await loginCallbacks.preLogin?.(phone);
                if (proceed === false) return; // if preLogin returns undefined or true, proceed with login
                return authManager
                    .sendSms({
                        reCaptcha: token,
                        phone,
                    })
                    .then((res) => {
                        setStep('otp');
                        setSmsRes(res);
                    })
                    .catch((err) => {
                        setPhoneErr(getError(err?.response?.data?.items));
                    });
            })
            .catch((err) => {
                promise.current?.reject?.(err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [phone, loading, validPhone]);

    const resetVariables = () => {
        setPhone('');
        setCode('');
        setPhoneErr(undefined);
    };

    const submitHandler = useCallback(() => {
        if (!smsRes || loading) {
            return;
        }

        setOptErr(undefined);
        setLoading(true);
        getRecaptchaToken()
            .then((token) => {
                if (!token) {
                    return Promise.reject(new Error('invalid token!'));
                }
                const vendor = VendorAPIManager.getInstance().getVendorCache(url);
                /* promise.current?.resolve?.(smsRes);return; */
                return authManager
                    .createToken({
                        otpCode: code,
                        otpID: smsRes.otpID,
                        reCaptcha: token,
                        restaurantUnique: vendor?.unique,
                        countryCode: vendor?.country?.code,
                    })
                    .then((res) => {
                        setAuth(res);

                        promise.current?.resolve?.(res);
                        if (!options?.preventClose) {
                            if (options?.closeTimeout) {
                                setTimeout(() => {
                                    setOpen(false);
                                }, options?.closeTimeout);
                            } else {
                                setOpen(false);
                            }
                        }
                        if (res.profile && res.isAuthorized) {
                            onLogin(true, res.profile);
                            setStep('done');
                            showToast(res.newUser);
                            loginCallbacks.postOTPVerification?.();
                            document.dispatchEvent(new CustomEvent(events.login));
                        }
                    })
                    .catch((err) => {
                        setOptErr(getError(err?.response?.data?.items));
                    });
            })
            .catch((err) => {
                promise.current?.reject?.(err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [smsRes, loading, phone, code, getRecaptchaToken]);

    const showToast = (isNewUSer: boolean | undefined) => {
        if (isNewUSer) {
            triggerAlternateSnackBar(t('Your qlub account is created!'));
        } else {
            triggerAlternateSnackBar(t('You are logged in successfully!'));
        }
        successfulLoginEvent('phone', isNewUSer);
    };

    const resetHandler = () => {
        setStep('phone');
        resetVariables();
    };

    const modalCloseHandler = () => {
        setOpen(false);
        resetHandler();
        promise.current?.reject?.('DRAWER_CLOSED');
    };

    const backHandler = () => {
        resetVariables();
        if (step === 'phone') {
            modalCloseHandler();
        } else {
            setStep('phone');
        }
    };

    const changePhoneHandler: ChangeHandler = (data) => {
        setPhone(data?.phone);
    };

    const phoneValidationHandler = (isValidNo: boolean | undefined) => {
        setIsValidPhone(isValidNo !== false);
    };

    const setCodeHandler = (val: string) => {
        setCode(val);
    };

    const getError = (errKey: string) => {
        switch (errKey) {
            case 'INVALID_PHONE':
                return t('INVALID_PHONE');
            case 'INVALID_CAPTCHA':
                return t('INVALID_CAPTCHA');
            case 'INVALID_CREDENTIALS':
                return t('INVALID_CREDENTIALS');
            default:
                return '';
        }
    };

    const handleAppleLogin = async (loginData: IAppleData) => {
        setAppleLoading(true);
        attemptToLoginEvent('apple');

        try {
            const response = await authManager.appleLogin({
                state: loginData.authorization.state || '',
                code: loginData.authorization.code,
                redirectURI: process.env.NEXT_PUBLIC_APPLE_REDIRECT_URL || '',
            });
            if (response) {
                modalCloseHandler();
                setAuth({
                    refresh: response.refresh,
                    JWTToken: response.access,
                    isAuthorized: response.profile && !response.profile.isGuest,
                });
                onLogin(true, response.profile);
                setAppleLoading(false);
                showToast(response.newUser);
                successfulLoginEvent('apple', response.newUser);
            }
        } catch (e) {
            console.log(e);
        }
    };

    const handleGoogleLoginInit = async () => {
        try {
            attemptToLoginEvent('google');
            setGoogleLoading(true);
            setRedirection(window.location.href);

            const baseUrl = window.location.origin;

            const googleLoginInit = await authManager.googleLoginInit({
                failureURL: `${baseUrl}/qr/${url.cc}/${url.slug}/oauth/google/fail`,
                successURL: `${baseUrl}/qr/${url.cc}/${url.slug}/oauth/google/success`,
            });

            if (googleLoginInit.loginURL) {
                window.location.href = googleLoginInit.loginURL;
            }
        } catch (e) {
            console.log(e);
        }
    };

    return {
        data: {
            appleLoading,
            googleLoading,
            key,
            phoneErr,
            optErr,
            resendTimer,
            loading,
            open,
            step,
            phone,
            smsRes,
            code,
            validPhone,
            hasConsented,
        },
        action: {
            resetHandler,
            submitHandler,
            loginHandler,
            resendSmsHandler,
            modalCloseHandler,
            backHandler,
            changePhoneHandler,
            setCodeHandler,
            openHandler,
            getError,
            handleAppleLogin,
            handleGoogleLoginInit,
            phoneValidationHandler,
            setHasConsented,
        },
    };
};
